import { useCallback, useEffect, useState } from 'react';
import { Wand2, Plus, Trash2, ChevronRight, Lock, Check } from 'lucide-react';
import { apiClient } from '../../lib/api-client';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';

interface SimpleEntity {
  id: string;
  displayName: string;
}

interface ExistingStream {
  streamId: string;
  displayName: string;
  products: SimpleEntity[];
  /** Unique team type ids across the stream (from nested products after server normalization). */
  teams: SimpleEntity[];
  /** Unique tenant codes across the stream. */
  tenants: SimpleEntity[];
}

interface RoleDef {
  code: string;
  agentTitle: string;
  agentDescription: string;
  productRefs: string[];
  teamRefs: string[];
  tenantRefs: string[];
  repo: string;
  adoProject: string;
  teamsTeamName: string;
  groupChatName: string;
  botEnabled: boolean;
  botDisplayName: string;
}

function titleCaseSegment(slug: string): string {
  return slug
    .split(/[-_/]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** ADO / Teams team + repo + group chat from stream/product/team/tenant refs (portal naming convention). */
function buildStandardOrgResourceNames(params: {
  streamId: string;
  streamDisplayName: string;
  productIds: string[];
  teamIds: string[];
  tenantIds: string[];
  products: (SimpleEntity & { mode: 'new' | 'existing' })[];
  teams: (SimpleEntity & { mode: 'new' | 'existing' })[];
  tenants: (SimpleEntity & { mode: 'new' | 'existing' })[];
  agentTitle: string;
}) {
  const {
    streamId,
    streamDisplayName,
    productIds,
    teamIds,
    tenantIds,
    products,
    teams,
    tenants,
    agentTitle,
  } = params;
  const pSorted = [...productIds].filter(Boolean).sort();
  const teamSorted = [...teamIds].filter(Boolean).sort();
  const tenSorted = [...tenantIds].filter(Boolean).sort();
  const repo = [streamId, ...pSorted, ...teamSorted, ...tenSorted].filter(Boolean).join('-');

  const tenantLabels = tenSorted.map((id) => tenants.find((x) => x.id === id)?.displayName || id);
  const productLabels = pSorted.map((id) => products.find((x) => x.id === id)?.displayName || id);
  const teamLabels = teamSorted.map((id) => teams.find((x) => x.id === id)?.displayName || id);
  const streamLabel = streamDisplayName.trim() || titleCaseSegment(streamId);
  const adoProject =
    [...tenantLabels, ...productLabels, ...teamLabels, streamLabel].join(' ').trim() || streamLabel;
  const teamsTeamName = adoProject;
  const groupChatName = repo ? `#${repo}` : '#';
  const botDisplayName = agentTitle.trim() || `${streamLabel} Bot`;

  return { repo, adoProject, teamsTeamName, groupChatName, botDisplayName };
}

interface Props {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const STEPS = ['Stream', 'Products', 'Teams', 'Tenants', 'Define Role', 'Review & Confirm'];

const emptyRole = (): RoleDef => ({
  code: '',
  agentTitle: '',
  agentDescription: '',
  productRefs: [],
  teamRefs: [],
  tenantRefs: [],
  repo: '',
  adoProject: '',
  teamsTeamName: '',
  groupChatName: '',
  botEnabled: true,
  botDisplayName: '',
});

export function OnboardStreamWizard({ open, onClose, onComplete }: Props) {
  const [step, setStep] = useState(0);
  const [existingStreams, setExistingStreams] = useState<ExistingStream[]>([]);

  const [streamMode, setStreamMode] = useState<'new' | 'existing'>('new');
  const [streamId, setStreamId] = useState('');
  const [displayName, setDisplayName] = useState('');

  const [products, setProducts] = useState<(SimpleEntity & { mode: 'new' | 'existing' })[]>([]);
  const [teams, setTeams] = useState<(SimpleEntity & { mode: 'new' | 'existing' })[]>([]);
  const [tenants, setTenants] = useState<(SimpleEntity & { mode: 'new' | 'existing' })[]>([]);
  const [role, setRole] = useState<RoleDef>(emptyRole());

  const [onboarding, setOnboarding] = useState(false);
  const [onboardResult, setOnboardResult] = useState<any>(null);
  const [onboardError, setOnboardError] = useState<string | null>(null);
  const [liveSteps, setLiveSteps] = useState<any[]>([]);

  const resetForm = useCallback(() => {
    setStep(0);
    setStreamMode('new');
    setStreamId('');
    setDisplayName('');
    setProducts([]);
    setTeams([]);
    setTenants([]);
    setRole(emptyRole());
    setOnboarding(false);
    setOnboardResult(null);
    setOnboardError(null);
    setLiveSteps([]);
  }, []);

  useEffect(() => {
    if (!open) return;
    resetForm();
    apiClient.get<{ existingStreams: ExistingStream[] }>('/api/admin/ai-onboarding/config')
      .then((res) => {
        setExistingStreams(res.existingStreams || []);
      })
      .catch(() => {});
  }, [open, resetForm]);

  const selectedStream = existingStreams.find((s) => s.streamId === streamId);

  const handleSelectExistingStream = (s: ExistingStream) => {
    setStreamMode('existing');
    setStreamId(s.streamId);
    setDisplayName(s.displayName);
    setProducts([]);
    setTeams([]);
    setTenants([]);
  };

  const handleNewStreamIdChange = (value: string) => {
    setStreamMode('new');
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setStreamId(sanitized);
    if (!displayName || displayName === streamId.charAt(0).toUpperCase() + streamId.slice(1)) {
      setDisplayName(sanitized.charAt(0).toUpperCase() + sanitized.slice(1));
    }
    setProducts([]);
    setTeams([]);
    setTenants([]);
  };

  // --- Products ---
  const toggleExistingProduct = (ep: SimpleEntity) => {
    setProducts((prev) => {
      const existing = prev.find((p) => p.id === ep.id && p.mode === 'existing');
      if (existing) return prev.filter((p) => !(p.id === ep.id && p.mode === 'existing'));
      return [...prev, { ...ep, mode: 'existing' as const }];
    });
  };

  const addNewProduct = () => {
    setProducts((prev) => [...prev, { id: '', displayName: '', mode: 'new' as const }]);
  };

  const removeProduct = (idx: number) => {
    if (products[idx].mode === 'existing') return;
    setProducts((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateProduct = (idx: number, field: 'id' | 'displayName', value: string) => {
    if (products[idx].mode === 'existing') return;
    setProducts((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  // --- Teams ---
  const toggleExistingTeam = (et: SimpleEntity) => {
    setTeams((prev) => {
      const existing = prev.find((t) => t.id === et.id && t.mode === 'existing');
      if (existing) return prev.filter((t) => !(t.id === et.id && t.mode === 'existing'));
      return [...prev, { ...et, mode: 'existing' as const }];
    });
  };

  const addNewTeam = () => {
    setTeams((prev) => [...prev, { id: '', displayName: '', mode: 'new' as const }]);
  };

  const removeTeam = (idx: number) => {
    if (teams[idx].mode === 'existing') return;
    setTeams((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateTeam = (idx: number, field: 'id' | 'displayName', value: string) => {
    if (teams[idx].mode === 'existing') return;
    setTeams((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  // --- Tenants ---
  const toggleExistingTenant = (et: SimpleEntity) => {
    setTenants((prev) => {
      const existing = prev.find((t) => t.id === et.id && t.mode === 'existing');
      if (existing) return prev.filter((t) => !(t.id === et.id && t.mode === 'existing'));
      return [...prev, { ...et, mode: 'existing' as const }];
    });
  };

  const addNewTenant = () => {
    setTenants((prev) => [...prev, { id: '', displayName: '', mode: 'new' as const }]);
  };

  const removeTenant = (idx: number) => {
    if (tenants[idx].mode === 'existing') return;
    setTenants((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateTenant = (idx: number, field: 'id' | 'displayName', value: string) => {
    if (tenants[idx].mode === 'existing') return;
    setTenants((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  // --- Role helpers ---
  const toggleRef = (field: 'productRefs' | 'teamRefs' | 'tenantRefs', id: string) => {
    setRole((prev) => {
      const arr = prev[field];
      return { ...prev, [field]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
    });
  };

  const hasNewContent =
    products.some((p) => p.mode === 'new') ||
    teams.some((t) => t.mode === 'new') ||
    tenants.some((t) => t.mode === 'new') ||
    !!role.code;

  const handleOnboard = async () => {
    setOnboarding(true);
    setOnboardError(null);
    setOnboardResult(null);
    setLiveSteps([]);

    /**
     * API expects nested OnboardingRequest.products (product → teamTypes → tenants).
     * Role refs filter the Cartesian set; empty refs mean “all selected” from prior steps.
     */
    const effectiveProductIds = (
      role.productRefs.length ? role.productRefs : products.map((p) => p.id)
    ).filter(Boolean);
    const effectiveTeamIds = (
      role.teamRefs.length ? role.teamRefs : teams.map((t) => t.id)
    ).filter(Boolean);
    const effectiveTenantIds = (
      role.tenantRefs.length ? role.tenantRefs : tenants.map((t) => t.id)
    ).filter(Boolean);

    const nestedProducts = effectiveProductIds.map((pid) => {
      const pRow = products.find((p) => p.id === pid);
      const productMode = pRow?.mode ?? 'existing';
      return {
        id: pid,
        displayName: pRow?.displayName || pid,
        teamTypes: effectiveTeamIds.map((tid) => {
          const tRow = teams.find((t) => t.id === tid);
          const teamMode = tRow?.mode ?? 'existing';
          return {
            id: tid,
            tenants: effectiveTenantIds.map((tcode) => {
              const tnRow = tenants.find((t) => t.id === tcode);
              const tenantMode = tnRow?.mode ?? 'existing';
              const anyNew = productMode === 'new' || teamMode === 'new' || tenantMode === 'new';
              const resourceMode = anyNew ? ('create' as const) : ('existing' as const);
              const teamsTeamName = role.teamsTeamName.trim();
              const groupChatName = role.groupChatName.trim();
              return {
                code: tcode,
                displayName: tnRow?.displayName || tcode,
                ...(role.agentTitle ? { agentTitle: role.agentTitle } : {}),
                ...(role.agentDescription ? { agentDescription: role.agentDescription } : {}),
                adoProject: { name: role.adoProject || '', mode: resourceMode },
                repo: { name: role.repo || '', mode: resourceMode },
                bot: role.botEnabled
                  ? { displayName: role.botDisplayName || '', mode: resourceMode }
                  : { displayName: '', mode: 'existing' as const },
                ...(teamsTeamName
                  ? {
                      teamsTeamName,
                      ...(resourceMode === 'create' ? { teamsTeamMode: 'create' as const } : {}),
                    }
                  : {}),
                ...(role.botEnabled && groupChatName
                  ? { groupChatName, groupChatMode: 'create' as const }
                  : role.botEnabled
                    ? { groupChatMode: 'skip' as const }
                    : { groupChatMode: 'skip' as const }),
              };
            }),
          };
        }),
      };
    });

    const body = {
      stream: streamId,
      streamMode: streamMode === 'new' ? 'create' : 'existing',
      displayName,
      products: nestedProducts,
    };

    try {
      let completeEventReceived = false;
      let hasStreamError = false;
      let latestSteps: any[] = [];
      const apiBase = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      };
      const accessToken = localStorage.getItem('oci_access_token');
      if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

      const response = await fetch(`${apiBase}/api/admin/ai-onboarding/stream`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errText = await response.text();
        setOnboardError(errText || `HTTP ${response.status}`);
        setOnboarding(false);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        setOnboardError('Streaming not supported');
        setOnboarding(false);
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              if (currentEvent === 'step') {
                const idx = latestSteps.findIndex((s) => s.id === parsed.id);
                if (idx >= 0) {
                  const updated = [...latestSteps];
                  updated[idx] = parsed;
                  latestSteps = updated;
                } else {
                  latestSteps = [...latestSteps, parsed];
                }
                setLiveSteps(latestSteps);
              } else if (currentEvent === 'complete') {
                completeEventReceived = true;
                setOnboardResult(parsed);
                onComplete?.();
              } else if (currentEvent === 'error') {
                hasStreamError = true;
                setOnboardError(parsed.error || 'Onboarding failed');
              }
            } catch { /* ignore parse errors */ }
            currentEvent = '';
          }
        }
      }

      if (!completeEventReceived && !hasStreamError && latestSteps.length > 0) {
        const failedCount = latestSteps.filter((s: any) => s.status === 'failed').length;
        const overallStatus = failedCount === 0 ? 'completed' : failedCount === latestSteps.length ? 'failed' : 'partial';
        setOnboardResult({ steps: latestSteps, overallStatus, streamId });
        onComplete?.();
      }
    } catch (err: unknown) {
      setOnboardError(err instanceof Error ? err.message : 'Onboarding failed');
    } finally {
      setOnboarding(false);
    }
  };

  const canProceed = (): boolean => {
    if (step === 0) return !!streamId && !!displayName;
    if (step === 1) return products.length > 0 && products.every((p) => !!p.id);
    if (step === 2) return teams.length > 0 && teams.every((t) => !!t.id);
    if (step === 3) return tenants.length > 0 && tenants.every((t) => !!t.id);
    if (step === 4) return !!role.code;
    return true;
  };

  const lastStepIdx = STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent maxWidth="4xl" className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-violet-500" />
            Onboard Role
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4 min-h-0 overflow-y-auto">
          {/* Step indicator */}
          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
            {STEPS.map((s, i) => (
              <span key={i} className="flex items-center gap-1">
                <span className={`px-2 py-0.5 rounded-full ${i === step ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 font-medium' : i < step ? 'text-gray-700 dark:text-gray-300' : ''}`}>
                  {s}
                </span>
                {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3" />}
              </span>
            ))}
          </div>

          {/* Step 0: Stream */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Create a new stream</label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Stream ID (e.g. delivery, sales)"
                    value={streamMode === 'new' ? streamId : ''}
                    onChange={(e) => handleNewStreamIdChange(e.target.value)}
                  />
                  <Input
                    placeholder="Display name"
                    value={streamMode === 'new' ? displayName : ''}
                    onChange={(e) => {
                      setStreamMode('new');
                      setDisplayName(e.target.value);
                    }}
                  />
                </div>
              </div>

              {existingStreams.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Or add to an existing stream</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {existingStreams.map((s) => (
                      <button
                        key={s.streamId}
                        type="button"
                        className={`text-left p-3 rounded-lg border-2 transition-colors ${
                          streamMode === 'existing' && streamId === s.streamId
                            ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                        onClick={() => handleSelectExistingStream(s)}
                      >
                        <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{s.displayName}</div>
                        <div className="text-xs font-mono text-gray-500 dark:text-gray-400">
                          {s.streamId} &middot; {s.products.length} product(s)
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {streamId && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {streamMode === 'new' ? 'A new stream will be created.' : `Adding to existing stream "${streamId}".`}
                </p>
              )}
            </div>
          )}

          {/* Step 1: Products */}
          {step === 1 && (
            <EntityPickerStep
              label="Products"
              existingEntities={selectedStream?.products || []}
              showExisting={streamMode === 'existing'}
              streamDisplayName={selectedStream?.displayName}
              items={products}
              onToggleExisting={toggleExistingProduct}
              onAddNew={addNewProduct}
              onRemove={removeProduct}
              onUpdate={updateProduct}
              idPlaceholder="Product ID (e.g. manage, maxiron)"
            />
          )}

          {/* Step 2: Teams */}
          {step === 2 && (
            <EntityPickerStep
              label="Teams"
              existingEntities={selectedStream?.teams || []}
              showExisting={streamMode === 'existing'}
              streamDisplayName={selectedStream?.displayName}
              items={teams}
              onToggleExisting={toggleExistingTeam}
              onAddNew={addNewTeam}
              onRemove={removeTeam}
              onUpdate={updateTeam}
              idPlaceholder="Team ID (e.g. dev, support)"
            />
          )}

          {/* Step 3: Tenants */}
          {step === 3 && (
            <EntityPickerStep
              label="Tenants"
              existingEntities={selectedStream?.tenants || []}
              showExisting={streamMode === 'existing'}
              streamDisplayName={selectedStream?.displayName}
              items={tenants}
              onToggleExisting={toggleExistingTenant}
              onAddNew={addNewTenant}
              onRemove={removeTenant}
              onUpdate={updateTenant}
              idPlaceholder="Tenant code (e.g. breedon, maxiron)"
              idLabel="Code"
            />
          )}

          {/* Step 4: Define Role */}
          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Define the role that ties together the products, teams, and tenants selected above.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Role Code *</label>
                  <Input
                    value={role.code}
                    onChange={(e) => setRole({ ...role, code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })}
                    placeholder="e.g. delivery-dev-breedon"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Agent Title</label>
                  <Input
                    value={role.agentTitle}
                    onChange={(e) => setRole({ ...role, agentTitle: e.target.value })}
                    maxLength={60}
                    placeholder="Title shown in agent UI"
                    className="mt-1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs text-gray-500 dark:text-gray-400">Agent Description</label>
                    <span className="text-[10px] text-gray-400">{role.agentDescription.length}/200</span>
                  </div>
                  <textarea
                    value={role.agentDescription}
                    onChange={(e) => setRole({ ...role, agentDescription: e.target.value })}
                    maxLength={200}
                    rows={3}
                    placeholder="Describe what this role's agent does"
                    className="mt-1 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
              </div>

              {/* Ref selectors */}
              <div className="space-y-3">
                <RefSelector
                  label="Product Refs"
                  allItems={products}
                  selectedIds={role.productRefs}
                  onToggle={(id) => toggleRef('productRefs', id)}
                />
                <RefSelector
                  label="Team Refs"
                  allItems={teams}
                  selectedIds={role.teamRefs}
                  onToggle={(id) => toggleRef('teamRefs', id)}
                />
                <RefSelector
                  label="Tenant Refs"
                  allItems={tenants}
                  selectedIds={role.tenantRefs}
                  onToggle={(id) => toggleRef('tenantRefs', id)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Repo Name</label>
                  <Input
                    value={role.repo}
                    onChange={(e) => setRole({ ...role, repo: e.target.value })}
                    placeholder="e.g. maxiron-delivery"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">ADO Project Name</label>
                  <Input
                    value={role.adoProject}
                    onChange={(e) => setRole({ ...role, adoProject: e.target.value })}
                    placeholder="e.g. delivery-breedon"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const effP = role.productRefs.length
                      ? role.productRefs
                      : products.map((p) => p.id).filter(Boolean);
                    const effT = role.teamRefs.length
                      ? role.teamRefs
                      : teams.map((t) => t.id).filter(Boolean);
                    const effTn = role.tenantRefs.length
                      ? role.tenantRefs
                      : tenants.map((t) => t.id).filter(Boolean);
                    const suggested = buildStandardOrgResourceNames({
                      streamId,
                      streamDisplayName: displayName,
                      productIds: effP,
                      teamIds: effT,
                      tenantIds: effTn,
                      products,
                      teams,
                      tenants,
                      agentTitle: role.agentTitle,
                    });
                    setRole((prev) => ({ ...prev, ...suggested }));
                  }}
                >
                  Apply standard names
                </Button>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Repo, ADO, Teams team, # group chat, and bot display name from stream / product / team / tenant selection.
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Teams team (display name)</label>
                  <Input
                    value={role.teamsTeamName}
                    onChange={(e) => setRole({ ...role, teamsTeamName: e.target.value })}
                    placeholder="e.g. Maxiron Sales"
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Group chat name</label>
                  <Input
                    value={role.groupChatName}
                    onChange={(e) => setRole({ ...role, groupChatName: e.target.value })}
                    placeholder="e.g. #maxiron-sales"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Bot config */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wide">Bot Configuration</label>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={role.botEnabled}
                      onChange={(e) => setRole({ ...role, botEnabled: e.target.checked })}
                      className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                    />
                    Enable Bot
                  </label>
                </div>
                {role.botEnabled && (
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Bot Display Name</label>
                    <Input
                      value={role.botDisplayName}
                      onChange={(e) => setRole({ ...role, botDisplayName: e.target.value })}
                      placeholder="e.g. Breedon Delivery Bot"
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Review */}
          {step === lastStepIdx && (
            <div className="space-y-4">
              {!onboardResult && !onboarding && (
                <>
                  <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                    <p><strong>Stream:</strong> {displayName} ({streamId}) &mdash; {streamMode === 'new' ? 'new' : 'existing'}</p>
                  </div>

                  <ReviewSection label="Products" items={products} />
                  <ReviewSection label="Teams" items={teams} />
                  <ReviewSection label="Tenants" items={tenants} />

                  <div className="text-sm space-y-1 ml-2">
                    <p className="font-medium text-gray-800 dark:text-gray-200">Role: {role.code || '(not set)'}</p>
                    {role.agentTitle && <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">Title: {role.agentTitle}</p>}
                    {role.agentDescription && <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">Description: {role.agentDescription}</p>}
                    {role.productRefs.length > 0 && <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">Products: {role.productRefs.join(', ')}</p>}
                    {role.teamRefs.length > 0 && <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">Teams: {role.teamRefs.join(', ')}</p>}
                    {role.tenantRefs.length > 0 && <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">Tenants: {role.tenantRefs.join(', ')}</p>}
                    {role.repo && <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">Repo: {role.repo}</p>}
                    {role.adoProject && <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">ADO Project: {role.adoProject}</p>}
                    {role.teamsTeamName && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">Teams team: {role.teamsTeamName}</p>
                    )}
                    {role.groupChatName && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">Group chat: {role.groupChatName}</p>
                    )}
                    {role.botEnabled && <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">Bot: {role.botDisplayName || '(default name)'}</p>}
                  </div>

                  {!hasNewContent && (
                    <p className="text-sm text-amber-600 dark:text-amber-400">
                      No new items to provision. Go back and add new entities or define a role.
                    </p>
                  )}
                </>
              )}

              {onboarding && liveSteps.length === 0 && (
                <div className="text-center py-8 text-sm text-gray-600 dark:text-gray-400">
                  Starting onboarding...
                </div>
              )}

              {onboarding && liveSteps.length > 0 && (
                <div className="space-y-2">
                  <Badge variant="default">in progress</Badge>
                  <LiveStepsList steps={liveSteps} />
                </div>
              )}

              {onboardResult && (
                <div className="space-y-2">
                  <Badge variant={onboardResult.overallStatus === 'completed' ? 'success' : onboardResult.overallStatus === 'partial' ? 'warning' : 'destructive'}>
                    {onboardResult.overallStatus}
                  </Badge>
                  <LiveStepsList steps={onboardResult.steps || liveSteps} />
                </div>
              )}

              {onboardError && (
                <p className="text-sm text-red-600 dark:text-red-400">{onboardError}</p>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          {onboardResult ? (
            <Button type="button" onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={step === 0 ? onClose : () => setStep(step - 1)} disabled={onboarding}>
                {step === 0 ? 'Cancel' : 'Back'}
              </Button>
              {step < lastStepIdx ? (
                <Button type="button" onClick={() => setStep(step + 1)} disabled={!canProceed()}>
                  Next
                </Button>
              ) : (
                <Button type="button" onClick={() => void handleOnboard()} disabled={onboarding || !hasNewContent}>
                  {onboarding ? 'Onboarding...' : 'Onboard'}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface EntityPickerStepProps {
  label: string;
  existingEntities: SimpleEntity[];
  showExisting: boolean;
  streamDisplayName?: string;
  items: (SimpleEntity & { mode: 'new' | 'existing' })[];
  onToggleExisting: (e: SimpleEntity) => void;
  onAddNew: () => void;
  onRemove: (idx: number) => void;
  onUpdate: (idx: number, field: 'id' | 'displayName', value: string) => void;
  idPlaceholder: string;
  idLabel?: string;
}

function EntityPickerStep({
  label,
  existingEntities,
  showExisting,
  streamDisplayName,
  items,
  onToggleExisting,
  onAddNew,
  onRemove,
  onUpdate,
  idPlaceholder,
  idLabel,
}: EntityPickerStepProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {showExisting
            ? `Select existing ${label.toLowerCase()} or add new ones:`
            : `Add ${label.toLowerCase()} for this stream:`}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={onAddNew}>
          <Plus className="w-4 h-4 mr-1" /> New {label.slice(0, -1)}
        </Button>
      </div>

      {showExisting && existingEntities.length > 0 && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Existing {label.toLowerCase()} in {streamDisplayName}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {existingEntities.map((e) => {
              const isSelected = items.some((i) => i.id === e.id && i.mode === 'existing');
              return (
                <button
                  key={e.id}
                  type="button"
                  className={`text-left p-3 rounded-lg border-2 transition-colors ${
                    isSelected
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                  onClick={() => onToggleExisting(e)}
                >
                  <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                    {e.displayName || e.id}
                  </div>
                  <div className="text-xs font-mono text-gray-500 dark:text-gray-400">{e.id}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {items.filter((i) => i.mode === 'new').length > 0 && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            New {label.toLowerCase()}
          </label>
          {items.map((item, idx) => {
            if (item.mode === 'existing') return null;
            return (
              <div key={idx} className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                <Input
                  placeholder={idPlaceholder}
                  value={item.id}
                  onChange={(e) => onUpdate(idx, 'id', e.target.value)}
                  className="flex-1"
                />
                <Input
                  placeholder="Display name"
                  value={item.displayName}
                  onChange={(e) => onUpdate(idx, 'displayName', e.target.value)}
                  className="flex-1"
                />
                <Button variant="ghost" size="sm" onClick={() => onRemove(idx)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {items.filter((i) => i.mode === 'existing').length > 0 && (
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Selected existing {label.toLowerCase()} (click above to deselect)
          </label>
          {items.filter((i) => i.mode === 'existing').map((item) => (
            <div key={item.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800">
              <Lock className="w-3 h-3 text-violet-500 shrink-0" />
              <span className="text-sm text-violet-700 dark:text-violet-300">{item.displayName || item.id}</span>
              <Badge variant="outline" className="text-xs ml-auto">existing</Badge>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && existingEntities.length === 0 && (
        <p className="text-xs text-gray-400 dark:text-gray-500 py-4 text-center">
          No {label.toLowerCase()} yet. Click &ldquo;New {label.slice(0, -1)}&rdquo; to begin.
        </p>
      )}
    </div>
  );
}

interface RefSelectorProps {
  label: string;
  allItems: (SimpleEntity & { mode: 'new' | 'existing' })[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}

function RefSelector({ label, allItems, selectedIds, onToggle }: RefSelectorProps) {
  if (allItems.length === 0) return null;
  return (
    <div>
      <label className="text-xs text-gray-500 dark:text-gray-400">{label}</label>
      <div className="flex flex-wrap gap-2 mt-1">
        {allItems.map((item) => {
          const selected = selectedIds.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors ${
                selected
                  ? 'border-violet-500 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300'
              }`}
              onClick={() => onToggle(item.id)}
            >
              {selected && <Check className="w-3 h-3" />}
              {item.displayName || item.id}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ReviewSectionProps {
  label: string;
  items: (SimpleEntity & { mode: 'new' | 'existing' })[];
}

function ReviewSection({ label, items }: ReviewSectionProps) {
  if (items.length === 0) return null;
  const newItems = items.filter((i) => i.mode === 'new');
  const existingItems = items.filter((i) => i.mode === 'existing');
  return (
    <div className="ml-2 text-sm space-y-1">
      <p className="font-medium text-gray-800 dark:text-gray-200">{label}</p>
      {existingItems.map((i) => (
        <p key={i.id} className="ml-4 text-xs text-violet-600 dark:text-violet-400">
          {i.displayName || i.id} <span className="text-violet-400 dark:text-violet-500">(existing)</span>
        </p>
      ))}
      {newItems.map((i) => (
        <p key={i.id} className="ml-4 text-xs text-green-700 dark:text-green-400">
          + {i.displayName || i.id} <span className="text-green-500 dark:text-green-600">(new)</span>
        </p>
      ))}
    </div>
  );
}

interface LiveStepsListProps {
  steps: any[];
}

function LiveStepsList({ steps }: LiveStepsListProps) {
  return (
    <div className="text-xs space-y-1 max-h-80 overflow-y-auto">
      {steps.map((s: any, i: number) => {
        const statusColor = s.status === 'completed' ? '#22c55e'
          : s.status === 'failed' ? '#ef4444'
          : s.status === 'running' ? '#3b82f6'
          : s.status === 'skipped' ? '#f59e0b'
          : '#9ca3af';
        const textClass = s.status === 'failed' ? 'text-red-600'
          : s.status === 'skipped' ? 'text-amber-600 dark:text-amber-400'
          : s.status === 'running' ? 'text-blue-600 dark:text-blue-400'
          : 'text-gray-600 dark:text-gray-400';
        return (
          <div key={i} className={`flex items-start gap-2 ${textClass}`}>
            <span className="w-2 h-2 rounded-full shrink-0 mt-1" style={{ backgroundColor: statusColor }}>
              {s.status === 'running' && (
                <span className="block w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: statusColor }} />
              )}
            </span>
            <div className="flex-1 min-w-0">
              <span>{s.name}</span>
              {s.detail && <span className="text-gray-400 ml-1">&mdash; {s.detail}</span>}
              {s.error && <span className="text-red-500 ml-1 block">&mdash; {s.error}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
